import { shuffle } from "lodash";
import { Configuration } from "Models/configuration";
import { Consumer, LeadOffsets } from "Models/consumer";
import { Message } from "Models/message";
import { Partition } from "Models/partition";
import { setConsumers } from "Redux/actions";
import { getConsumers, getCurrentTick } from "Redux/selectors";
import store, { dispatch } from "Redux/store";
import { Subscription } from "rxjs";
import { addMessage, getActiveLeadLog } from "./logs.service";
import { mainTimeline$ } from "./timeline.service";
import { getActiveLeads, updateOffsets } from "./partitions.service";
import { takeEvery } from "./utils/take-every";

type OffsetsTuple = [msg: Message, id: number];
let consumerSubs$: Map<string, Subscription[]> = new Map<
    string,
    Subscription[]
>();

export const getAllConsumers = () => getConsumers(store.getState());

export const getConsumer = (uid: string) =>
    getAllConsumers().find((c) => c.logUid === uid);

export const setupConsumers = (
    { consumers }: Partial<Configuration>,
    existing: Consumer[],
    allPartitions: Partition[]
) => {
    const activeLeads = getActiveLeads(allPartitions).sort(
        (a, b) => a.id - b.id
    );
    return consumers?.reduce((acc: Consumer[], cc, idx) => {
        const groupConsumers = consumers?.filter(
            ({ group }) => group === cc.group
        );
        const indexInGroup = groupConsumers.indexOf(cc);

        const leads = activeLeads.filter(
            (_, i) => i % groupConsumers.length === indexInGroup
        );

        const consumer = existing[idx] ?? new Consumer();
        const offsets = leads.reduce(
            (m: LeadOffsets, l: Partition) => ({
                ...m,
                [l.id]:
                    consumer.offsets?.[l.id] ?? l.offset[consumer.group] ?? 0,
            }),
            {}
        );
        return [
            ...acc,
            {
                ...consumer,
                id: idx + 1,
                group: cc.group,
                offsets,
                leads: leads.map((l) => l.id),
                interval: cc.consumerInterval,
                commitOffsetStep: cc.commitOffsetStep,
            },
        ];
    }, []);
};

export const startConsumers = (consumers: Consumer[]) => {
    stopConsumers();
    const map = consumers.map((c) => {
        const values$ = mainTimeline$.pipe(takeEvery(c.interval));

        const consumptionSub$ = values$.subscribe((_) => consume(c.logUid));

        const commitSub$ = values$
            .pipe(takeEvery(c.commitOffsetStep))
            .subscribe((_) => {
                commitOffsets(c.logUid);
            });

        return [c.logUid, [commitSub$, consumptionSub$]] as [
            string,
            Subscription[]
        ];
    });
    consumerSubs$ = new Map(map);
};

export const stopConsumers = () => {
    consumerSubs$.forEach((subs) => subs.forEach((s) => s.unsubscribe()));
};

export const consume = (uid: string) => {
    const consumer = getConsumer(uid);
    if (!consumer) return;

    const nextValue = getNextValue(consumer);

    if (nextValue) {
        const [msg, id] = nextValue;
        addMessage(uid, {
            ...msg,
            tick: getCurrentTick(store.getState()),
        });
        updateConsumer({
            ...consumer,
            recentMessage: msg,
            offsets: {
                ...consumer.offsets,
                [id]: msg.id,
            },
        });
    }
};

export const commitOffsets = (uid: string) => {
    const consumer = getConsumer(uid);
    if (!consumer) return;
    updateOffsets(consumer.offsets, consumer.group);
};

export const updateConsumer = (consumer: Consumer) => {
    const all = getAllConsumers().map((c) =>
        c.logUid === consumer.logUid ? consumer : c
    );
    dispatch(setConsumers(all));
};

const getNextValue = (consumer: Consumer) => {
    const nextValues = consumer.leads.reduce(
        (acc: OffsetsTuple[], id: number) => {
            const log = getActiveLeadLog(id);
            const val = log
                .filter((m) => m.id > consumer.offsets[id])
                .slice(0, 1)
                .map((msg) => [msg, id] as OffsetsTuple)
                .shift();
            return val ? [...acc, val] : acc;
        },
        []
    );
    return shuffle(nextValues).shift();
};
